/** RFC 7807 body the API returns for every refusal, plus its `violations` extension. */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  traceId?: string
  violations?: { field: string; message: string }[]
  /** Set on a 401 step-up challenge (ADR 0011): the level to sign in at, `mfa`. */
  acrValues?: string
}

export interface OrganizationSummary {
  id: string
  name: string
  slug: string
  /** Owner | Member */
  memberRole: string
  accessEndsUtc: string | null
}

/** Response of GET /api/me. */
export interface MeResponse {
  userId: string
  subject: string
  email: string | null
  displayName: string | null
  userName: string | null
  roles: string[]
  organizations: OrganizationSummary[]
  firstLogin: boolean
  /** False when this session signed in with a password alone (ADR 0011). */
  multiFactor: boolean
  /** Whether room owners may let participants in without two-step verification. */
  mfaExemptionsEnabled: boolean
}

// ------------------------------------------------------------------ organizations

export interface OrganizationResponse {
  /** Audit retention in days; null keeps everything. */
  auditRetentionDays: number | null
  id: string
  name: string
  slug: string
  status: string
  createdUtc: string
  memberCount: number
  roomCount: number
}

export interface UserSummary {
  id: string
  email: string | null
  displayName: string | null
}

// ------------------------------------------------------------------ permissions

/** The ten bits of the brief, always as the server answered them. */
export interface PermissionSet {
  view: boolean
  download: boolean
  print: boolean
  upload: boolean
  editMetadata: boolean
  delete: boolean
  managePermissions: boolean
  askQuestion: boolean
  answerQuestion: boolean
  viewAudit: boolean
}

export const PERMISSION_KEYS: (keyof PermissionSet)[] = [
  'view',
  'download',
  'print',
  'upload',
  'editMetadata',
  'delete',
  'managePermissions',
  'askQuestion',
  'answerQuestion',
  'viewAudit',
]

export const PERMISSION_LABELS: Record<keyof PermissionSet, string> = {
  view: 'View',
  download: 'Download',
  print: 'Print',
  upload: 'Upload',
  editMetadata: 'Edit metadata',
  delete: 'Delete',
  managePermissions: 'Manage permissions',
  askQuestion: 'Ask questions',
  answerQuestion: 'Answer questions',
  viewAudit: 'View audit',
}

export const NO_PERMISSIONS: PermissionSet = {
  view: false,
  download: false,
  print: false,
  upload: false,
  editMetadata: false,
  delete: false,
  managePermissions: false,
  askQuestion: false,
  answerQuestion: false,
  viewAudit: false,
}

export type NodeType = 'Room' | 'Folder' | 'Document'
export type PrincipalType = 'User' | 'Group'

export interface PermissionRowResponse {
  id: number
  nodeType: NodeType
  nodeId: string
  nodeName: string | null
  nodeIndex: string | null
  isInherited: boolean
  principalType: PrincipalType
  principalId: string
  principalName: string | null
  principalEmail: string | null
  permissions: PermissionSet
  expiresUtc: string | null
  isExpired: boolean
  createdUtc: string
  modifiedUtc: string | null
}

export interface EffectivePermissionsResponse {
  nodeType: NodeType
  nodeId: string
  isMember: boolean
  isOwner: boolean
  roomStatus: string | null
  source: string
  permissions: PermissionSet
  granted: string[]
}

export interface SetPermissionRequest {
  principalType: PrincipalType
  principalId: string
  permissions: PermissionSet
  expiresUtc: string | null
}

// ------------------------------------------------------------------ rooms

export type RoomStatus = 'Draft' | 'Open' | 'Frozen' | 'Archived'

export interface RoomSummary {
  id: string
  name: string
  description: string | null
  status: RoomStatus
  watermarkEnabled: boolean
  createdUtc: string
  memberRole: 'Owner' | 'Participant'
  accessEndsUtc: string | null
  organizationId: string
  organizationName: string
  documentCount: number
  memberCount: number
}

export interface RoomResponse {
  id: string
  name: string
  description: string | null
  status: RoomStatus
  watermarkEnabled: boolean
  createdUtc: string
  modifiedUtc: string | null
  frozenUtc: string | null
  archivedUtc: string | null
  organizationId: string
  organizationName: string
  memberRole: 'Owner' | 'Participant' | null
  permissions: PermissionSet
  /** The NDA gate. When required and not accepted, every permission above is false. */
  nda: NdaState
  /**
   * The organization's privacy and data use policy (ADR 0008). When required and not accepted
   * the permissions above are untouched, but the API refuses uploads and answers to requests
   * with a 403 on field `dataPolicy`; the text is shown where the upload control would be.
   * Never required of a room owner.
   */
  dataPolicy: DataPolicyState
}

export interface DataPolicyState {
  required: boolean
  accepted: boolean
  versionId: string | null
  versionNumber: number | null
  title: string | null
  body: string | null
  setUtc: string | null
  acceptedUtc: string | null
}

/** The field a policy refusal names, so it can be told from a 403 for a missing permission. */
export const DATA_POLICY_FIELD = 'dataPolicy'

export const NO_DATA_POLICY: DataPolicyState = {
  required: false,
  accepted: false,
  versionId: null,
  versionNumber: null,
  title: null,
  body: null,
  setUtc: null,
  acceptedUtc: null,
}

export interface DataPolicyAcceptanceResponse {
  userId: string
  email: string | null
  displayName: string | null
  acceptedUtc: string
  ipAddress: string | null
  userAgent: string | null
  /** The room the person was in when they accepted; the acceptance holds in every room of the organization. */
  roomId: string | null
  roomName: string | null
}

export interface DataPolicyVersionResponse {
  id: string
  versionNumber: number
  title: string
  createdUtc: string
  retiredUtc: string | null
  isCurrent: boolean
  setBy: string | null
  acceptanceCount: number
  acceptances: DataPolicyAcceptanceResponse[]
}

/** The organization's policy for its Owners: the current text (null when there is none) and every version with who accepted it. */
export interface DataPolicyResponse {
  current: {
    id: string
    versionNumber: number
    title: string
    body: string
    setUtc: string
    setBy: string | null
  } | null
  versions: DataPolicyVersionResponse[]
}

export interface NdaState {
  required: boolean
  accepted: boolean
  versionId: string | null
  versionNumber: number | null
  title: string | null
  body: string | null
  setUtc: string | null
  acceptedUtc: string | null
}

export interface NdaAcceptanceResponse {
  userId: string
  email: string | null
  displayName: string | null
  acceptedUtc: string
  ipAddress: string | null
  userAgent: string | null
}

export interface NdaVersionResponse {
  id: string
  versionNumber: number
  title: string
  createdUtc: string
  retiredUtc: string | null
  isCurrent: boolean
  setBy: string | null
  acceptanceCount: number
  acceptances: NdaAcceptanceResponse[]
}

export interface NdaRecordResponse {
  versions: NdaVersionResponse[]
}

export interface RoomMemberResponse {
  userId: string
  email: string | null
  displayName: string | null
  userStatus: string
  memberRole: 'Owner' | 'Participant'
  accessEndsUtc: string | null
  createdUtc: string
  groups: string[]
  /** When another owner let this member in without two-step verification (ADR 0011, owners since §9); null when it is required. */
  mfaExemptUtc: string | null
  mfaExemptByName: string | null
}

export interface GroupMemberResponse {
  userId: string
  email: string | null
  displayName: string | null
  accessEndsUtc: string | null
}

export interface GroupResponse {
  id: string
  name: string
  keycloakGroupPath: string | null
  createdUtc: string
  members: GroupMemberResponse[]
}

// ------------------------------------------------------------------ folders and documents

export interface FolderResponse {
  id: string
  parentId: string | null
  name: string
  indexNumber: string
  depth: number
  position: number
  createdUtc: string
  documentCount: number
  permissions: PermissionSet
}

export interface FolderCreatedResponse {
  id: string
  indexNumber: string
  created: boolean
}

export interface DocumentResponse {
  id: string
  name: string
  folderId: string | null
  createdUtc: string
  modifiedUtc: string | null
  currentVersionNumber: number | null
  currentVersionId: string | null
  contentType: string | null
  sizeBytes: number | null
  sha256: string | null
  currentUploadedUtc: string | null
  versionCount: number
  pendingVersions: number
  refusedVersions: number
  permissions: PermissionSet
  /** The checklist items this document answers (ADR 0006). */
  checklistItems: ChecklistTagResponse[]
}

// ------------------------------------------------------------------ checklist (ADR 0006, 0007)

/**
 * The six states of a request (ADR 0007 s4). The responder's moves are InProgress, Submitted and
 * NotApplicable; the owner's are NeedsRevision, Completed, NotApplicable and reopening to Open.
 */
export type ChecklistItemStatus =
  'Open' | 'InProgress' | 'Submitted' | 'NeedsRevision' | 'Completed' | 'NotApplicable'

/** The workbook's cover-sheet bucket for a state. */
export type ChecklistStatusRollup = 'Not started' | 'WIP' | 'Done' | 'N/A'

export type ChecklistPriority = 'High' | 'Medium' | 'Low'
export type ChecklistRequestKind = 'Static' | 'Dynamic'

export interface ChecklistSectionResponse {
  id: string
  code: string
  name: string
  description: string | null
  /** The template's folder index ("1.2"): a hint for people, not a link to a folder. */
  vdrIndexHint: string | null
  serviceProvider: string | null
  docLabelPrefix: number
  position: number
  /**
   * The folder this section's answers go into (ADR 0009 s10), its live index and its name: set
   * only when the owner linked one AND the caller may view it. Optional, as every Phase 2 field
   * here is: an API rolled back to before them sends none, and the screen must not throw.
   */
  vdrFolderId?: string | null
  vdrIndex?: string | null
  vdrFolderName?: string | null
}

export interface ChecklistAssignee {
  type: 'User' | 'Group'
  id: string
  name: string | null
  /** Assignment is not a grant: this person holds no upload bit anywhere, so cannot submit what they owe. */
  cannotSubmit: boolean
}

export interface ChecklistItemResponse {
  id: string
  /** The short line a list shows; the request itself is requestText. */
  title: string
  description: string | null
  /** Order inside its section (or among the items that have none). */
  position: number
  status: ChecklistItemStatus
  submittedUtc: string | null
  submittedBy: string | null
  completedUtc: string | null
  completedBy: string | null
  createdUtc: string
  modifiedUtc: string | null
  /** Linked documents the caller may see. */
  documentCount: number
  sectionId: string | null
  category: string | null
  /** The stable label correspondence cites ("2035"); never reused, never renumbered. */
  docLabel: number | null
  priority: ChecklistPriority | null
  requestKind: ChecklistRequestKind | null
  requestText: string | null
  /** The requesting side's standing note; read-only to a responder. */
  providerNotes: string | null
  /** Why the item is NeedsRevision or NotApplicable. */
  statusReason: string | null
  statusRollup: ChecklistStatusRollup
  // ---- accountability (ADR 0009); all optional, see ChecklistSectionResponse.vdrFolderId
  /** The responding side's answer in words. For some requests it is the whole deliverable. */
  responderNotes?: string | null
  /** When the notes were last written; sent back as expectedNotesUtc so two people cannot overwrite each other. */
  responderNotesUtc?: string | null
  responderNotesBy?: string | null
  /** yyyy-MM-dd: a date, not an instant. */
  requestedOn?: string | null
  goalDate?: string | null
  /** The responder is late: goal date passed and the item is still Open, InProgress or NeedsRevision. */
  isOverdue?: boolean
  assignedToMe?: boolean
  /** Null for a caller who may not see who is assigned: they learn only assignedToMe (ADR 0009 s5). */
  assignees?: ChecklistAssignee[] | null
  relatedItem?: { id: string; docLabel: number | null; title: string } | null
  /** The blank form to fill in; null unless the caller may view that document. */
  formDocument?: { id: string; name: string } | null
}

export interface ChecklistResponse {
  canManage: boolean
  canSubmit: boolean
  /** May see and set who is assigned: an owner, or manage_permissions on the room. */
  canAssign?: boolean
  /** In reading order: section by section, then the items that have no section. */
  items: ChecklistItemResponse[]
  sections: ChecklistSectionResponse[]
}

export interface ChecklistTagResponse {
  id: string
  title: string
  status: ChecklistItemStatus
  docLabel: number | null
}

/** What an owner writes on an item. */
export interface ChecklistItemBody {
  title: string
  description: string | null
  sectionId: string | null
  category: string | null
  priority: ChecklistPriority | null
  requestKind: ChecklistRequestKind | null
  requestText: string | null
  providerNotes: string | null
  /** yyyy-MM-dd, or null to clear. Sent with includesTrackingFields (ADR 0009 s15). */
  requestedOn?: string | null
  goalDate?: string | null
  relatedItemId?: string | null
  formDocumentId?: string | null
}

export interface ChecklistPrincipal {
  type: 'User' | 'Group'
  id: string
}

export type ChecklistBulkField =
  'priority' | 'category' | 'goalDate' | 'requestedOn' | 'sectionId' | 'assignees'

/** One edit for many items, all or nothing. A field is written only if named in `fields`, and then as given (null clears). */
export interface ChecklistBulkBody {
  itemIds: string[]
  fields: ChecklistBulkField[]
  priority?: ChecklistPriority | null
  category?: string | null
  goalDate?: string | null
  requestedOn?: string | null
  sectionId?: string | null
  assignees?: ChecklistPrincipal[]
}

export interface ChecklistHistoryEntry {
  eventUtc: string
  action: string
  outcome: string
  actor: string | null
  detailJson: string | null
}

export interface ChecklistSectionBody {
  code: string
  name: string
  description: string | null
  vdrIndexHint: string | null
  serviceProvider: string | null
}

export interface InstantiateChecklistResponse {
  template: string
  sectionCount: number
  itemCount: number
  checklist: ChecklistResponse
}

export interface DocumentVersionResponse {
  id: string
  versionNumber: number
  contentType: string
  sizeBytes: number | null
  sha256: string | null
  scanStatus: 'Pending' | 'Clean' | 'Infected' | 'Failed'
  scanDetail: string | null
  uploadedUtc: string
  scannedUtc: string | null
  uploadedBy: string | null
  uploadedByEmail: string | null
  comment: string | null
  isCurrent: boolean
}

export interface DocumentDetailResponse {
  id: string
  name: string
  folderId: string | null
  folderIndexNumber: string | null
  folderName: string | null
  createdUtc: string
  modifiedUtc: string | null
  createdBy: string | null
  currentVersionNumber: number | null
  currentVersionId: string | null
  contentType: string | null
  sizeBytes: number | null
  sha256: string | null
  permissions: PermissionSet
  versions: DocumentVersionResponse[]
  checklistItems: ChecklistTagResponse[]
}

export interface UploadResponse {
  documentId: string
  versionId: string
  versionNumber: number
  name: string
  sizeBytes: number
  sha256: string
  scanStatus: 'Pending' | 'Clean' | 'Infected' | 'Failed'
  scanDetail: string | null
  isCurrent: boolean
}

export interface ZipUploadResponse {
  foldersCreated: number
  files: UploadResponse[]
  skipped: string[]
}

// ------------------------------------------------------------------ invitations

export interface CreateInvitationRequest {
  email: string
  memberRole: 'Participant' | 'Owner'
  realmRole: 'reviewer' | 'contributor' | 'room-owner'
  accessEndsUtc: string | null
  groupId: string | null
  message: string | null
}

export interface InvitationCreatedResponse {
  id: string
  email: string
  state: 'Pending' | 'Accepted'
  outcome: 'invited' | 'renewed' | 'added'
  accountCreated: boolean
  emailed: boolean
}

export interface InvitationResponse {
  id: string
  email: string
  memberRole: 'Participant' | 'Owner'
  realmRole: string
  accessEndsUtc: string | null
  groupId: string | null
  groupName: string | null
  message: string | null
  expiresUtc: string
  state: 'Pending' | 'Expired' | 'Accepted' | 'Revoked'
  accountCreated: boolean
  sendCount: number
  lastSentUtc: string | null
  createdUtc: string
  createdBy: string | null
  acceptedUtc: string | null
  acceptedUserId: string | null
  revokedUtc: string | null
}

// ------------------------------------------------------------------ Q&A

export type QuestionStatus = 'Open' | 'Answered' | 'Closed'

export interface AnswerResponse {
  id: string
  body: string
  answeredUtc: string
  answeredById: string | null
  answeredByName: string | null
  answeredByEmail: string | null
}

export interface QuestionResponse {
  id: string
  number: number
  targetType: NodeType
  targetId: string
  targetName: string
  targetIndex: string | null
  targetDeleted: boolean
  subject: string
  /** Present on the single-question read, absent in the list. */
  body: string | null
  status: QuestionStatus
  askedUtc: string
  askedById: string | null
  askedByName: string | null
  askedByEmail: string | null
  assignedToId: string | null
  assignedToName: string | null
  publishedToAll: boolean
  publishedUtc: string | null
  answeredUtc: string | null
  closedUtc: string | null
  submitterGroups: string | null
  publishedGroups: string | null
  answerCount: number
  lastAnswerUtc: string | null
  isMine: boolean
  /** The server's answer: the caller holds answer_question on the question's node. */
  canAnswer: boolean
  visibleBecause: 'owner' | 'asker' | 'group' | 'published'
  answers: AnswerResponse[] | null
}

export interface AskQuestionRequest {
  targetType: NodeType
  targetId: string | null
  subject: string
  body: string
}

// ------------------------------------------------------------------ search

export interface SearchHitResponse {
  documentId: string
  name: string
  folderId: string | null
  folderIndex: string | null
  folderName: string | null
  versionNumber: number
  contentType: string | null
  sizeBytes: number | null
  modifiedUtc: string
  matchedName: boolean
  matchedText: boolean
  snippet: string | null
  textStatus: 'Pending' | 'Extracting' | 'Done' | 'Unsupported' | 'Failed' | 'None'
}

export interface SearchResponse {
  query: string
  /** fulltext on Azure SQL, like where the server has no full-text component, none when nothing searchable was typed. */
  mode: 'fulltext' | 'like' | 'none'
  hits: SearchHitResponse[]
}

// ------------------------------------------------------------------ reports

export interface ReportWindow {
  fromUtc: string | null
  toUtc: string | null
  generatedUtc: string
}

export interface ActivityEventResponse {
  eventId: number
  eventUtc: string
  action: string
  outcome: 'Success' | 'Denied' | 'Failure'
  actorId: string | null
  actorName: string | null
  actorEmail: string | null
  targetType: string | null
  targetId: string | null
  targetName: string | null
  ipAddress: string | null
  userAgent: string | null
  correlationId: string | null
  detail: string | null
}

export interface ActivityReportResponse {
  window: ReportWindow
  total: number
  skip: number
  take: number
  events: ActivityEventResponse[]
}

export interface UserActivityResponse {
  userId: string
  displayName: string | null
  email: string | null
  memberRole: 'Owner' | 'Participant'
  accessEndsUtc: string | null
  expired: boolean
  groups: string | null
  views: number
  downloads: number
  searches: number
  questions: number
  denied: number
  events: number
  firstActivityUtc: string | null
  lastActivityUtc: string | null
}

export interface DocumentActivityResponse {
  documentId: string
  name: string
  folderId: string | null
  folderIndex: string | null
  folderName: string | null
  versionNumber: number | null
  sizeBytes: number | null
  viewers: number
  views: number
  downloads: number
  denied: number
  lastActivityUtc: string | null
}

export interface GroupActivityResponse {
  groupId: string | null
  groupName: string
  members: number
  activeMembers: number
  views: number
  downloads: number
  searches: number
  questions: number
  lastActivityUtc: string | null
}

export interface TableReportResponse<T> {
  window: ReportWindow
  rows: T[]
}

// ------------------------------------------------------------------ notifications

export interface NotificationPreferences {
  digestEnabled: boolean
  qaEnabled: boolean
  expiryEnabled: boolean
  lastDigestUtc: string | null
  /** Request-list mail (ADR 0009): assigned, sent back, submitted, goal-date reminders. On by default. */
  checklistEnabled?: boolean
}

// ------------------------------------------------------------------ close-out packages

export interface ArchiveResponse {
  id: string
  status: 'Requested' | 'Building' | 'Ready' | 'Failed'
  roomStatusAtBuild: string | null
  sizeBytes: number | null
  /** SHA-256 of the zip; the download sends the same value in X-Content-Sha256. */
  sha256: string | null
  entryCount: number | null
  attempts: number
  error: string | null
  requestedUtc: string
  builtUtc: string | null
  requestedBy: string | null
}
